/**
 * FieldPalettePanel — left sidebar for AdvancedFormBuilder.
 * Contains the element palette (Elements / Styles / Settings tabs).
 * Extracted from AdvancedFormBuilder.js Phase 3 split.
 */
import React from 'react';
import { useFormBuilder } from './FormBuilderContext';

export function FieldPalettePanel() {
  const {
    sidebarCollapsed, setSidebarCollapsed,
    activeTab, setActiveTab,
    elementTypes, globalStyles, setGlobalStyles,
    currentSection,
    addFormElement, addGlobalElement,
    handleDragStart,
    backgroundImageInputRef,
    handleBackgroundImageUpload,
    removeBackgroundImage,
    handleSectionBackgroundUpload,
    removeSectionBackgroundImage,
  } = useFormBuilder();

  return (
    <div className={`elements-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <h3>Form Builder</h3>
        <button
          className="collapse-sidebar"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          {sidebarCollapsed ? '→' : '←'}
        </button>
      </div>

      {!sidebarCollapsed && (
        <>
          <div className="sidebar-tabs">
            <button className={`tab-button ${activeTab === 'elements' ? 'active' : ''}`} onClick={() => setActiveTab('elements')}>
              🧩 Elements
            </button>
            <button className={`tab-button ${activeTab === 'styles' ? 'active' : ''}`} onClick={() => setActiveTab('styles')}>
              🎨 Styles
            </button>
            <button className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`} onClick={() => setActiveTab('settings')}>
              ⚙️ Settings
            </button>
          </div>

          <div className="tab-content">
            {activeTab === 'elements' && (
              <div className="elements-categories">
                <div className="category">
                  <h4>🌍 Global Elements</h4>
                  <div className="element-buttons">
                    <button className="element-button global-element-btn" onClick={() => addGlobalElement('image')} title="Add image outside sections">
                      <span className="element-icon">🖼️</span> Global Image
                    </button>
                    <button className="element-button global-element-btn" onClick={() => addGlobalElement('video')} title="Add video outside sections">
                      <span className="element-icon">🎥</span> Global Video
                    </button>
                    <button className="element-button global-element-btn" onClick={() => addGlobalElement('header')} title="Add header outside sections">
                      <span className="element-icon">🏷️</span> Global Header
                    </button>
                  </div>
                </div>

                {['basic', 'advanced', 'layout', 'action'].map(category => (
                  <div key={category} className="category">
                    <h4>
                      {category === 'basic' && '📋 Basic'}
                      {category === 'advanced' && '🚀 Advanced'}
                      {category === 'layout' && '🎨 Layout'}
                      {category === 'action' && '⚡ Actions'}
                    </h4>
                    <div className="element-buttons">
                      {elementTypes
                        .filter(el => el.category === category)
                        .map(element => (
                          <button
                            key={element.type}
                            className="element-button"
                            draggable
                            onDragStart={(e) => handleDragStart(e, element.type, 'sidebar')}
                            onClick={() => addFormElement(element.type)}
                          >
                            <span className="element-icon">{element.icon}</span>
                            {element.label}
                          </button>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'styles' && (
              <div className="styles-panel">
                <h4>Background Images</h4>
                <div className="background-upload-section">
                  <div className="background-upload-group">
                    <label>Form Background:</label>
                    <div className="background-upload-controls">
                      <button className="upload-background-btn" onClick={() => backgroundImageInputRef.current?.click()}>
                        📁 Upload Background
                      </button>
                      {globalStyles.backgroundImage && (
                        <button className="remove-background-btn" onClick={() => removeBackgroundImage('global')}>
                          🗑️ Remove
                        </button>
                      )}
                    </div>
                    <input
                      ref={backgroundImageInputRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleBackgroundImageUpload(e, 'global')}
                    />
                    {globalStyles.backgroundImage && (
                      <div className="background-preview">
                        <div className="preview-image" style={{ backgroundImage: globalStyles.backgroundImage, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                      </div>
                    )}
                  </div>

                  {currentSection && (
                    <div className="background-upload-group">
                      <label>Section Background:</label>
                      <div className="background-upload-controls">
                        <button className="upload-background-btn" onClick={() => {
                          const input = document.createElement('input');
                          input.type = 'file'; input.accept = 'image/*';
                          input.onchange = (e) => handleSectionBackgroundUpload(e, currentSection.id);
                          input.click();
                        }}>
                          📁 Upload Background
                        </button>
                        {currentSection.styling.backgroundImage && (
                          <button className="remove-background-btn" onClick={() => removeSectionBackgroundImage(currentSection.id)}>
                            🗑️ Remove
                          </button>
                        )}
                      </div>
                      {currentSection.styling.backgroundImage && (
                        <div className="background-preview">
                          <div className="preview-image" style={{ backgroundImage: currentSection.styling.backgroundImage, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <h4>Quick Styles</h4>
                <div className="style-presets">
                  <button className="style-preset" onClick={() => setGlobalStyles({ ...globalStyles, backgroundColor: '#ffffff', color: '#2d3748', backgroundImage: '' })}>
                    Light Theme
                  </button>
                  <button className="style-preset" onClick={() => setGlobalStyles({ ...globalStyles, backgroundColor: '#1a202c', color: '#ffffff', backgroundImage: '' })}>
                    Dark Theme
                  </button>
                  <button className="style-preset" onClick={() => setGlobalStyles({ ...globalStyles, backgroundColor: '#f7fafc', color: '#2d3748', backgroundImage: '' })}>
                    Modern Theme
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="settings-panel">
                <h4>Form Settings</h4>
                <div className="setting-group">
                  <label>Form Title:</label>
                  <input type="text" placeholder="Enter form title" onChange={(e) => setGlobalStyles({ ...globalStyles, formTitle: e.target.value })} />
                </div>
                <div className="setting-group">
                  <label>Max Width:</label>
                  <input type="text" value={globalStyles.maxWidth} onChange={(e) => setGlobalStyles({ ...globalStyles, maxWidth: e.target.value })} />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
